import {
  Flex,
  Box,
  Text,
  Button,
  Skeleton,
  Group,
  useMantineTheme,
} from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { closeModal, openModal } from "@mantine/modals";
import { memo, useEffect, useCallback } from "react";
import { BiSearch } from "react-icons/bi";
import {
  IAttendeeSelect,
  useCatalogContext,
} from "src/api/context/CatalogContext";
import TextInput from "src/components/TextInput/TextInput";
import {
  AttendeeType,
  useGetAttendeePageCustomerLazyQuery,
} from "src/generated/graphql";
import GuestProfile from "src/modules/Client/GuestLeadProfile";
import { ModalId } from "src/utils/constants";

export const attendeeDetail = (attendee: IAttendeeSelect) => {
  const { attendeeType, client } = attendee || {};
  let name, memberStatus, profilePicUrl;
  if (attendeeType === AttendeeType.Client) {
    name = `${client!.user.firstName} ${client!.user.lastName}`;
    memberStatus = client?.memberStatus;
    profilePicUrl = client?.user.profilePicUrl;
  }
  return {
    name,
    memberStatus,
    profilePicUrl,
  };
};

function SelectMember() {
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];
  const { setselectedCustomer, selectedAttendeeId, setselectedAttendeeId } =
    useCatalogContext();
  const [search, setSearch] = useDebouncedState("", 500);

  useEffect(() => {
    getSearchValues();
  }, [search]);

  const [getAttendees, { data, loading }] = useGetAttendeePageCustomerLazyQuery(
    {
      fetchPolicy: "network-only",
    }
  );

  const handleSuccessGuest = (attendeeId: string) => {
    setselectedAttendeeId(attendeeId);
  };

  const handleSelect = () => {
    if (!selectedAttendeeId || !data?.attendeePage?.items) return;
    setselectedCustomer(
      data?.attendeePage?.items.find((e) => e.id === selectedAttendeeId) || null
    );
    closeModal(ModalId.CheckoutCustomer);
  };

  const handleAddGuest = useCallback(() => {
    openModal({
      modalId: ModalId.CreateGuest,
      closeOnClickOutside: false,
      title: "Add Guest",
      children: <GuestProfile onSuccess={handleSuccessGuest} />,
    });
  }, []);

  const getSearchValues = () => {
    getAttendees({
      variables: {
        pagination: {
          count: 8,
          orderBy: { field: "attendee_createdDate", direction: "DESC" },
          filters: [
            {
              field: "name",
              type: "text",
              value: search,
              parentRelationField: "user",
            },
          ],
        },
      },
    });
  };

  return (
    <>
      <TextInput
        m={"8px"}
        radius={"xl"}
        size="sm"
        icon={<BiSearch />}
        placeholder="Search"
        defaultValue={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
      />

      {!data?.attendeePage.items.length && (
        <Box mb={"sm"} p={7} bg={theme.colors.secondary[4]}>
          {loading ? (
            <Skeleton
              w="100%"
              height={60}
              mb={10}
              sx={{
                "::before": {
                  backgroundColor: `${theme.colors.secondary[3]} !important`,
                },
                "::after": {
                  backgroundColor: `${theme.colors.secondary[2]} !important`,
                },
              }}
            />
          ) : (
            <Text align="center" fw={600} size={18}>
              No Member Found
            </Text>
          )}
        </Box>
      )}

      {data?.attendeePage.items.map((c) => {
        const { name, memberStatus, profilePicUrl } = attendeeDetail(c);
        return (
          <Flex
            key={c.id}
            p={7}
            gap={"xs"}
            sx={{
              color:
                selectedAttendeeId === c.id
                  ? theme.colors.primaryTextColor[6]
                  : theme.colors.secondaryTextColor[6],
              backgroundColor:
                selectedAttendeeId === c.id
                  ? theme.colors.primary[6]
                  : "transparent",
              borderBottom: `1px solid ${theme.colors.secondary[1]}`,
              borderRadius: selectedAttendeeId === c.id ? 4 : undefined,
              "&:hover": {
                color:
                  selectedAttendeeId === c.id
                    ? theme.colors.primaryTextColor[6]
                    : theme.colors.secondaryTextColor[1],
                backgroundColor:
                  selectedAttendeeId === c.id
                    ? theme.colors.primary[6]
                    : theme.colors.secondary[1],
                borderRadius: 4,
              },
            }}
            onClick={() => setselectedAttendeeId(c.id)}
          >
            <Box
              sx={(theme) => ({
                height: 50,
                width: 50,
                background: theme.colors.secondary[6],
                borderRadius: 4,
              })}
            >
              {profilePicUrl && (
                <img
                  src={profilePicUrl}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                    borderRadius: 4,
                    maxHeight: 50,
                    maxWidth: 50,
                  }}
                  alt="profile pic"
                />
              )}
            </Box>
            <Box>
              <Text size={14}>{name}</Text>
              <Text size={14} fw={500}>
                {memberStatus}
              </Text>
            </Box>
          </Flex>
        );
      })}

      <Group>
        <Button
          fullWidth
          mt={"xs"}
          radius={"xl"}
          size="sm"
          variant="outline"
          onClick={handleAddGuest}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[3],
            },
          }}
        >
          Add Guest
        </Button>
        <Button
          fullWidth
          mt={"xs"}
          radius={"xl"}
          size="sm"
          onClick={handleSelect}
          disabled={!selectedAttendeeId}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          Select
        </Button>
      </Group>
    </>
  );
}

export default memo(SelectMember);
